/**
 * export-transactions-excel.ts
 *
 * Exports project transactions to a formatted Excel (.xlsx) file.
 *
 * Behaviour:
 *  - Only the columns that are currently visible (per the user's column
 *    configuration) are exported, in the same order they appear on screen.
 *  - In live mode, all pages are fetched via cursor-based pagination before
 *    building the workbook (safety-capped at 50 pages / 50,000 rows).
 *  - In demo mode, the caller passes the already-filtered in-memory array.
 *  - Styled with the same branded header row and alternating-row colours
 *    used by export-projects-excel.ts.
 *  - Includes an "Export Info" metadata sheet.
 *  - File is named "Project_Transactions_Export_YYYY-MM-DD.xlsx".
 *
 * Dependencies:
 *  - exceljs (already in package.json)
 */

import ExcelJS from 'exceljs';
import type { CostingTransactionHeader } from '@/components/transactions-grid';
import {
  queryProjectTransactions,
  type ProjectTransactionQueryBody,
  type ProjectTransactionDto,
} from '@/lib/data-service';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import { mapDtoToHeader } from '@/pages/project-transactions';
import { formatDate } from '@/lib/utils';

// ── Brand colours (matching export-projects-excel.ts) ────────────────────────

const BRAND_BLUE  = '1F3864';
const HEADER_TEXT  = 'FFFFFF';
const ALT_ROW_BG   = 'F5F7FA';

// ── Safety limits ────────────────────────────────────────────────────────────

const MAX_PAGES = 50;
const MAX_ROWS  = 50_000;

// ── Column header labels ─────────────────────────────────────────────────────

const HEADER_LABELS: Record<string, string> = {
  projectCode: 'Project Code',
  projectName: 'Project Name',
  accountCode: 'Account Code',
  accountName: 'Account Name',
  date: 'Date',
  transactionType: 'Type',
  origin: 'Origin',
  resourceName: 'Resource',
  quantity: 'Quantity',
  price: 'Price',
  value: 'Value',
  batched: 'Batched',
  uplift: 'Uplift %',
  upliftValue: 'Uplift Value',
  priceWithUplift: 'Price + Uplift',
  valueWithUplift: 'Value + Uplift',
  actualSales: 'Actual Sales',
  actualCost: 'Actual Cost',
  currencyCode: 'Currency',
  resourceCode: 'Resource Code',
  employeeCode: 'Employee Code',
  invoiceAccountCode: 'Invoice Account Code',
  invoiceAccountName: 'Invoice Account Name',
  supplierAccountCode: 'Supplier Code',
  postType: 'Post Type',
  projectStatus: 'Project Status',
  isInvoiced: 'Invoiced',
  periodNumber: 'Period',
  yearLabel: 'Year',
  weekNo: 'Week',
  detail: 'Detail',
  headerRef: 'Header Ref',
  internalRef: 'Internal Ref',
  costCentreCode: 'Cost Centre Code',
  costCentreName: 'Cost Centre',
  costingSortKey: 'Costing Sort Key',
  salesOrderNumber: 'Sales Order',
  purchaseOrderNumber: 'Purchase Order',
  batchRef: 'Batch Ref',
  enteredBy: 'Entered By',
  dateEntered: 'Date Entered',
  stockCode: 'Stock Code',
  stockName: 'Stock Name',
  analysisCode: 'Analysis Code',
  analysisName: 'Analysis',
  nominalCode: 'Nominal Code',
};

// ── Column width hints ───────────────────────────────────────────────────────

const COLUMN_WIDTHS: Record<string, number> = {
  projectCode: 14,
  projectName: 32,
  accountCode: 14,
  accountName: 28,
  date: 14,
  transactionType: 18,
  origin: 18,
  resourceName: 22,
  quantity: 12,
  price: 14,
  value: 16,
  batched: 10,
  uplift: 12,
  upliftValue: 14,
  priceWithUplift: 16,
  valueWithUplift: 16,
  actualSales: 16,
  actualCost: 16,
  currencyCode: 10,
  resourceCode: 14,
  employeeCode: 14,
  invoiceAccountCode: 18,
  invoiceAccountName: 22,
  supplierAccountCode: 16,
  postType: 10,
  projectStatus: 14,
  isInvoiced: 10,
  periodNumber: 10,
  yearLabel: 10,
  weekNo: 10,
  detail: 32,
  headerRef: 16,
  internalRef: 14,
  costCentreCode: 16,
  costCentreName: 20,
  costingSortKey: 16,
  salesOrderNumber: 14,
  purchaseOrderNumber: 16,
  batchRef: 14,
  enteredBy: 16,
  dateEntered: 14,
  stockCode: 14,
  stockName: 22,
  analysisCode: 14,
  analysisName: 20,
  nominalCode: 14,
};

// ── Column classification ────────────────────────────────────────────────────

const CURRENCY_IDS = new Set([
  'value', 'upliftValue', 'priceWithUplift', 'valueWithUplift',
  'actualSales', 'actualCost',
]);

const NUMERIC_IDS = new Set([
  'quantity', 'price', 'uplift', 'internalRef',
  'periodNumber', 'weekNo', 'salesOrderNumber',
  'userNumber1', 'userNumber2',
]);

const BOOLEAN_IDS = new Set(['batched', 'isInvoiced', 'userFlag1', 'userFlag2']);
const DATE_IDS = new Set(['date', 'dateEntered', 'userDate1', 'userDate2']);

const RIGHT_ALIGN_IDS = new Set([
  ...CURRENCY_IDS,
  ...NUMERIC_IDS,
]);

// ── Fetch all transaction pages ──────────────────────────────────────────────

/**
 * Loops through all pages of the transactions endpoint via cursor-based
 * pagination. Stops when `hasNextPage` is false, items are empty, or
 * the safety limit is reached.
 */
export async function fetchAllTransactionPages(
  buildBody: (cursor: string | null) => ProjectTransactionQueryBody,
): Promise<CostingTransactionHeader[]> {
  const allItems: CostingTransactionHeader[] = [];
  let cursor: string | null = null;
  let page = 0;

  while (page < MAX_PAGES && allItems.length < MAX_ROWS) {
    const body = buildBody(cursor);
    const response = await queryProjectTransactions(body);

    if (!response.items || response.items.length === 0) break;

    const mapped = response.items.map(
      (dto: ProjectTransactionDto) => mapDtoToHeader(dto),
    );
    allItems.push(...mapped);

    if (!response.pageInfo?.hasNextPage) break;
    cursor = response.pageInfo.endCursor;
    page++;
  }

  return allItems;
}

// ── Cell value resolver ──────────────────────────────────────────────────────

function getCellValue(
  columnId: string,
  tx: CostingTransactionHeader,
): string | number | boolean {
  const raw = (tx as unknown as Record<string, unknown>)[columnId];

  if (DATE_IDS.has(columnId)) {
    return formatDate(raw as string);
  }
  if (CURRENCY_IDS.has(columnId)) {
    return typeof raw === 'number' ? raw : 0;
  }
  if (NUMERIC_IDS.has(columnId)) {
    return typeof raw === 'number' ? raw : 0;
  }
  if (BOOLEAN_IDS.has(columnId)) {
    return raw ? 'Yes' : 'No';
  }

  // Special: valueWithUplift is computed (quantity * priceWithUplift)
  if (columnId === 'valueWithUplift') {
    return tx.quantity * tx.priceWithUplift;
  }

  if (raw === undefined || raw === null) return '';
  return String(raw);
}

// ── Main export function ─────────────────────────────────────────────────────

export async function exportTransactionsToExcel(
  allTransactions: CostingTransactionHeader[],
  columnDefs: ColumnDefinition[],
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Project Management';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Transactions', {
    views: [{ state: 'frozen', ySplit: 1 }],
    pageSetup: {
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
    },
  });

  // Visible columns in display order
  const visibleCols = columnDefs
    .filter((c) => c.visible)
    .sort((a, b) => a.order - b.order);

  // Set column widths
  sheet.columns = visibleCols.map((_col, idx) => ({
    key: String(idx),
    width: COLUMN_WIDTHS[_col.id] ?? 18,
  }));

  // ── Header row ─────────────────────────────────────────────────────────────
  const headerRow = sheet.addRow(
    visibleCols.map((col) => HEADER_LABELS[col.id] ?? col.name),
  );
  headerRow.height = 22;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 11 };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BRAND_BLUE },
    };
    cell.alignment = {
      horizontal: RIGHT_ALIGN_IDS.has(visibleCols[colNumber - 1]?.id) ? 'right' : 'left',
      vertical: 'middle',
    };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'CCCCCC' } },
    };
  });

  // ── Data rows ──────────────────────────────────────────────────────────────
  allTransactions.forEach((tx, rowIdx) => {
    const values = visibleCols.map((col) => getCellValue(col.id, tx));
    const row = sheet.addRow(values);

    // Alternating row background
    if (rowIdx % 2 === 1) {
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: ALT_ROW_BG },
        };
      });
    }

    // Apply number formatting per column
    row.eachCell((cell, colNumber) => {
      const colId = visibleCols[colNumber - 1]?.id;
      if (!colId) return;

      if (CURRENCY_IDS.has(colId)) {
        cell.numFmt = '#,##0.00';
      }
      if (NUMERIC_IDS.has(colId) && colId !== 'internalRef') {
        cell.numFmt = '#,##0.##';
      }
      if (RIGHT_ALIGN_IDS.has(colId)) {
        cell.alignment = { horizontal: 'right' };
      }
    });
  });

  // ── Metadata sheet ─────────────────────────────────────────────────────────
  const meta = workbook.addWorksheet('Export Info');
  meta.addRow(['Field', 'Value']);
  meta.addRow(['Exported On', new Date().toLocaleString('en-GB')]);
  meta.addRow(['Total Rows', allTransactions.length]);
  meta.addRow(['Columns Exported', visibleCols.length]);
  meta.getRow(1).font = { bold: true };
  meta.columns = [{ width: 22 }, { width: 40 }];

  // ── Trigger browser download ───────────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const filename = `Project_Transactions_Export_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
